// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

export const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL || '').trim();
export const SUPABASE_PUBLISHABLE_KEY = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '').trim();

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    'Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in your .env.local file.'
  );
}

// Resilient wrapper around supabase-js's Navigator LockManager auth lock.
// The default lock throws an unhandled rejection when contention exceeds 10s
// (e.g. a tab in the OAuth callback flow holding the lock while another tab
// boots). For a single-tab SPA, falling back to running unlocked is safer
// than rejecting — duplicate token refreshes are idempotent server-side,
// but a rejected boot blocks the user. Logs to console so the rare event is
// still observable via Sentry breadcrumbs once that's live.
const safeAuthLock = async <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return fn();
  }
  try {
    return (await navigator.locks.request(
      name,
      { mode: 'exclusive', signal: AbortSignal.timeout(acquireTimeout) },
      async () => fn(),
    )) as R;
  } catch (err) {
    // AbortError (timeout) or NotSupportedError — degrade gracefully rather
    // than surfacing a 10-second auth boot failure to the user.
    console.warn(
      `[supabase] auth lock "${name}" unavailable (${(err as Error)?.name ?? 'unknown'}); proceeding unlocked`,
    );
    return fn();
  }
};

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    lock: safeAuthLock,
  }
});

/**
 * Force-clear any stale Supabase auth state from localStorage AND
 * issue a best-effort signOut (raced against a 1.5s timeout so it
 * cannot hang the caller).
 *
 * Why: when a refresh_token in localStorage is invalidated (token
 * rotation, server-side revocation, user signed in on another device),
 * supabase-js silently retries the refresh on every operation. The
 * `navigator.locks` lock that serializes auth ops gets held by the
 * hung refresh attempt, so subsequent calls — including signOut and
 * signInWithPassword — wait forever on the lock.
 *
 * Symptoms when this happens:
 *   - Sign-in button shows a spinner that never resolves
 *   - Network panel shows bursts of POST to /auth/v1/token with 4xx/429
 *   - Users report "I had to clear cache and 3p cookies to log in"
 *
 * The fix (per Supabase issue #18981 + community reports): bypass the
 * lock by directly removing the `sb-{ref}-auth-token*` keys from
 * localStorage. Then signOut works on fresh state.
 */
export async function forceClearSupabaseAuth(): Promise<void> {
  // Direct localStorage purge — sb-* keys are stable across supabase-js
  // versions. Includes the chunked token (.0, .1, ...) and PKCE verifier.
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("sb-") && (k.endsWith("-auth-token") || k.includes("-auth-token."))) {
        keysToRemove.push(k);
      }
      if (k && k.startsWith("sb-") && k.endsWith("-auth-token-code-verifier")) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // Storage unavailable (private mode / quota) — best effort.
  }

  // Best-effort signOut so the in-memory client state matches the
  // wiped storage. Race against 1.5s so a held lock can't stick.
  try {
    await Promise.race([
      supabase.auth.signOut({ scope: "local" }),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  } catch {
    // signOut can throw on network failure — local storage is already
    // wiped, so the next operation starts from a clean state regardless.
  }
}